import { Spin } from 'antd';
import parsePhoneNumberFromString, { ParseError } from 'libphonenumber-js';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import CorporateDashboard from '@/components/Corporate/CorporateDashboard';
import CorporateLogoHorizontal from '@/components/Corporate/CorporateLogoHorizontal';
import CorporateOnboarding from '@/components/Corporate/CorporateOnboarding';
import { IntlTelInput } from '@/components/shared/IntlTelInput';

import { authModalControl } from '@/atoms/authModalControl';
import { getGlobalCountry } from '@/atoms/countryState';
import { user } from '@/atoms/user';
import { createOrganizationLead } from '@/services/Organization';
import { getUserProfileDirect } from '@/services/user';
import { User } from '@/utils/interfaces/User';

const CorporateHubPage: React.FC = () => {
  const router = useRouter();
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [, setAuthModal] = useRecoilState(authModalControl);
  const [loading, setLoading] = useState(true);
  const [submittingLead, setSubmittingLead] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const globalCountry = getGlobalCountry();
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    globalCountry?.countryCode?.toLowerCase() || 'in',
  );
  const [phonePrefix, setPhonePrefix] = useState(
    globalCountry?.phoneCode || '91',
  );

  const validatePhone = (cleanPhone: string, prefix: string) => {
    if (!cleanPhone.trim()) {
      setPhoneError('Phone number is required');
      return false;
    }
    try {
      const cleanPrefix = prefix.replace(/\D/g, '') || '91';
      const parsedPhoneNumber = parsePhoneNumberFromString(
        '+' + cleanPrefix + cleanPhone,
      );

      if (!parsedPhoneNumber) {
        setPhoneError('Invalid phone number format');
        return false;
      } else if (!parsedPhoneNumber.isValid()) {
        setPhoneError('Invalid phone number. Please check the number and try again.');
        return false;
      } else {
        setPhoneError('');
        return true;
      }
    } catch (error) {
      if (error instanceof ParseError) {
        setPhoneError(error.message);
      } else {
        setPhoneError('An error occurred while validating the phone number. Please try again.');
      }
      return false;
    }
  };

  useEffect(() => {
    const checkUserOrg = async () => {
      try {
        if (!userData) {
          const profile = await getUserProfileDirect();
          if (profile?.data) {
            setUserData(profile.data);
          }
        }
      } catch {
        // Not logged in or error
      } finally {
        setLoading(false);
      }
    };

    checkUserOrg();
  }, [userData]);

  useEffect(() => {
    if (globalCountry?.countryCode && (!phoneCountryCode || phoneCountryCode === 'in')) {
      setPhoneCountryCode(globalCountry.countryCode.toLowerCase());
    }
    if (globalCountry?.phoneCode && (!phonePrefix || phonePrefix === '91')) {
      setPhonePrefix(globalCountry.phoneCode);
    }
  }, [globalCountry]);

  if (loading) {
    return (
      <div className='min-h-screen bg-[#070707] text-white flex items-center justify-center'>
        <Spin size='large' />
      </div>
    );
  }

  const handleGetFreeYear = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submittingLead) return;

    const cleanedPhone = phone.replace(/\D/g, '');
    const isPhoneValid = validatePhone(cleanedPhone, phonePrefix);
    if (!isPhoneValid) return;

    setSubmittingLead(true);
    try {
      const urlParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : null;
      const invitedBy =
        (router.query.invited_by as string) || urlParams?.get('invited_by');

      const prefix = phonePrefix
        ? phonePrefix.startsWith('+')
          ? phonePrefix
          : `+${phonePrefix}`
        : '+91';

      const payload: {
        country_code: string;
        phone: string;
        invited_by?: string;
      } = {
        country_code: prefix,
        phone: phone.trim(),
      };

      if (invitedBy) {
        payload.invited_by = invitedBy;
      }

      await createOrganizationLead(payload);
    } catch (error) {
      console.error('Error submitting organization lead:', error);
    } finally {
      setSubmittingLead(false);
      setAuthModal((prev) => ({
        ...prev,
        signUp: true,
        login: false,
        forgotPassword: false,
        noRedirect: true,
        email: '',
        phone: phone.trim(),
        phoneCountryCode: phoneCountryCode,
        phonePrefix: phonePrefix,
      }));
    }
  };

  const renderFeatureList = () => (
    <div className='space-y-5 max-w-lg w-full'>
      {/* Feature 1 */}
      <div className='flex items-start gap-4'>
        <div className='mt-1 text-primary shrink-0'>
          <svg
            className='w-5 h-5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={1.8}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
            />
          </svg>
        </div>
        <div>
          <h3 className='text-xs font-semibold text-white'>
            1 Year Free Access
          </h3>
          <p className='text-xs text-grayZl-400 mt-0.5'>
            Explore unlimited audiobooks.
          </p>
        </div>
      </div>

      <div className='border-t border-grayZl-850' />

      {/* Feature 2 */}
      <div className='flex items-start gap-4'>
        <div className='mt-1 text-primary shrink-0'>
          <svg
            className='w-5 h-5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={1.8}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
            />
          </svg>
        </div>
        <div>
          <h3 className='text-xs font-semibold text-white'>
            Unlimited Digital Short Books
          </h3>
          <p className='text-xs text-grayZl-400 mt-0.5'>
            Give your organization access to the digital library.
          </p>
        </div>
      </div>

      <div className='border-t border-grayZl-850' />

      {/* Feature 3 */}
      <div className='flex items-start gap-4'>
        <div className='mt-1 text-primary shrink-0'>
          <svg
            className='w-5 h-5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={1.8}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
            />
          </svg>
        </div>
        <div>
          <h3 className='text-xs font-semibold text-white'>
            Built for Teams
          </h3>
          <p className='text-xs text-grayZl-400 mt-0.5'>
            Enable learning across departments and employees.
          </p>
        </div>
      </div>
    </div>
  );

  // If user is not signed in
  if (!userData) {
    return (
      <div className='min-h-screen bg-bgColor text-white flex items-center justify-center px-4 py-8 md:px-8 md:px-12'>
        <Head>
          <title>ZebraLearn Business | Start Your Free Year</title>
        </Head>

        <div className='w-full max-w-[400px] md:max-w-[966px] mx-auto flex flex-col md:flex-row gap-8 md:gap-12 items-center md:items-stretch'>
          {/* Left Info Section */}
          <div className='flex-1 w-full flex flex-col justify-center'>
            {/* Logo */}
            <div className='mb-6 flex justify-start'>
              <CorporateLogoHorizontal />
            </div>

            {/* Heading */}
            <h1 className='text-base md:text-[20px] font-bold text-white leading-tight tracking-tight mb-4'>
              Give Your Team Unlimited Digital Learning for 1 Year{' '}
              <span className='text-primary'>Free</span>
            </h1>

            {/* Tagline */}
            <div className='flex items-center gap-2 mb-6 md:mb-8'>
              <span className='w-1 h-4 bg-primary rounded-full inline-block' />
              <span className='text-xs font-semibold tracking-wider text-grayZl-400 uppercase'>
                BUILT FOR SMARTER ORGANIZATIONS
              </span>
            </div>

            {/* Feature List on Desktop */}
            <div className='hidden md:block'>
              {renderFeatureList()}
            </div>
          </div>

          {/* Right Card */}
          <div className='flex-1 w-full md:self-stretch flex flex-col items-center md:items-stretch'>
            <div className='w-full md:flex-1 flex flex-col justify-center bg-transparent md:bg-grayZl-950 border-0 rounded-r-xl p-0 md:p-8 shadow-none md:shadow-2xl backdrop-blur-md'>
              <h2 className='hidden md:block text-2xl font-semibold text-white tracking-tight w-full'>
                Start Your Free Year
              </h2>
              <p className='hidden md:block text-xs md:text-sm text-grayZl-400 mt-1.5 mb-6'>
                Get your organization started with 1 year of free access.
              </p>

              <form onSubmit={handleGetFreeYear} className='space-y-4 w-full'>
                <div>
                  <IntlTelInput
                    id='corporate-phone-input'
                    placeholder='Enter mobile number'
                    phone={phone}
                    phoneCountryCode={phoneCountryCode}
                    status={phoneError ? 'error' : ''}
                    onChange={({
                      phone: newPhone,
                      phoneCountryCode: newCode,
                      phonePrefix: newPrefix,
                    }) => {
                      const cleaned = newPhone.replace(/^0+/, '');
                      setPhone(cleaned);
                      if (newCode) setPhoneCountryCode(newCode);
                      if (newPrefix) setPhonePrefix(newPrefix);
                      if (phoneError) {
                        validatePhone(cleaned, newPrefix || phonePrefix);
                      }
                    }}
                    inputClassName='!bg-grayZl-900 !border-grayZl-850 !text-white placeholder:!text-grayZl-500 !rounded-xl !py-3'
                  />
                  {phoneError && (
                    <p className='text-red-500 text-xs mt-1.5 text-left'>{phoneError}</p>
                  )}
                </div>

                <button
                  type='submit'
                  disabled={submittingLead}
                  className='w-full h-12 bg-primary hover:bg-darkPrimary active:scale-[0.99] text-white font-semibold rounded-lg text-sm uppercase tracking-wider transition-all duration-150 flex items-center justify-center cursor-pointer shadow-lg disabled:opacity-75 disabled:cursor-not-allowed'
                >
                  {submittingLead ? (
                    <Spin size='small' className='[&_.ant-spin-dot-item]:bg-white' />
                  ) : (
                    'GET 1 YEAR FREE'
                  )}
                </button>
              </form>

              {/* Sub-note */}
              <div className='flex items-center justify-center gap-1.5 mt-3.5 text-xs text-grayZl-400'>
                <svg
                  className='w-3.5 h-3.5 text-grayZl-400 shrink-0'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z'
                    clipRule='evenodd'
                  />
                </svg>
                <span>No payment required to get started.</span>
              </div>
            </div>

            {/* Feature List on Mobile (appears below the card) */}
            <div className='block md:hidden mt-8 w-full max-w-[400px]'>
              {renderFeatureList()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userOrg = (userData as any)?.organization;

  // If organization exists and user is admin -> show dashboard
  if (userOrg && userOrg.role === 'admin') {
    return <CorporateDashboard />;
  }

  // If organization exists and user is member -> show member dashboard
  if (userOrg && userOrg.role === 'member') {
    return <CorporateDashboard />;
  }

  // If organization is not present under user -> Show onboarding / "Set up your business profile"
  return (
    <CorporateOnboarding
      onComplete={async () => {
        try {
          const fresh = await getUserProfileDirect();
          if (fresh?.data) {
            setUserData(fresh.data);
          }
        } catch (err) {
          // Ignore refresh profile error
        }
        router.push('/corporate/dashboard');
      }}
    />
  );
};

export default CorporateHubPage;
